import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { summariseBoard, type SeverityCounts } from '../lib/alerts-api';
import { useAlertsBoard } from '../lib/alerts-board';

/**
 * AlertsDashboardWidget — ADR-071 A2 counts-by-source dashboard widget.
 *
 * Built to `ADR-071-DASHBOARD-WIDGET-MOCKUP.html`: a one-line "state of the
 * nation" headline (N critical · N warning · N info across all sources) plus a
 * per-source breakdown. Each source row links to ITS OWN dedicated surface — the
 * platform alerts detail view, the existing monitoring page, the snippet
 * telemetry view — counts are consolidated but no remediation workflow is
 * flattened (A3). Read-only glance + link-through; no actions here.
 *
 * Reads the ONE shared alerts board (lib/alerts-board.tsx, quality register Q1), so it refreshes
 * with the navbar and the Alerts page. When a zone did not answer it says so above the counts
 * (Q2) — a partial headline must not read as "all clear".
 */

interface SourceRow {
  name: string;
  counts: SeverityCounts;
  dest: string;
  to: string;
}

function Counts({ counts }: { counts: SeverityCounts }) {
  const cell = (n: number, cls: string, label: string) => (
    <span className={`aw-c${n === 0 ? ' zero' : ''}`}>
      <span className={`d ${n === 0 ? '' : cls}`} />
      {n} {label}
    </span>
  );
  return (
    <div className="aw-counts">
      {cell(counts.critical, 'dot-crit', 'critical')}
      {cell(counts.warning, 'dot-warn', 'warning')}
      {cell(counts.info, 'dot-info', 'info')}
    </div>
  );
}

export function AlertsDashboardWidget() {
  const { status, alerts, failedZones } = useAlertsBoard();
  const summary = useMemo(() => summariseBoard(alerts), [alerts]);

  // The widget is best-effort glance — until the board has been read, render nothing rather
  // than a broken card (the navbar indicator is the urgent path).
  if (status !== 'loaded') return null;

  const { total, bySource } = summary;
  const partial = failedZones.length > 0;
  // "All clear" is a claim about every zone — never made while a zone is missing.
  const allClear = !partial && total.critical === 0 && total.warning === 0 && total.info === 0;

  // ⚠ Read every source DEFENSIVELY. `bySource` comes from the API, and the
  // portal (Cloudflare Pages) and the Lambda that computes the summary deploy
  // SEPARATELY — so there is a real window where a new portal talks to an API
  // that predates a newly-added source and omits its key. Indexing straight into
  // it would throw inside render and blank the whole widget: the ops dashboard
  // would go dark precisely during a deploy. A missing source reads as zero.
  const at = (k: keyof typeof bySource): SeverityCounts =>
    bySource?.[k] ?? { critical: 0, warning: 0, info: 0 };

  const rows: SourceRow[] = [
    {
      name: 'Nexa Platform',
      counts: {
        critical: at('scoring').critical + at('deploy').critical + at('auth').critical,
        warning: at('scoring').warning + at('deploy').warning + at('auth').warning,
        info: at('scoring').info + at('deploy').info + at('auth').info,
      },
      dest: 'scoring · auth · deploy → /admin/alerts',
      to: '/admin/alerts',
    },
    {
      name: 'API',
      counts: at('vendor'),
      dest: 'vendor up/down · load bands → /admin/monitoring',
      to: '/admin/monitoring',
    },
    {
      name: 'Snippet',
      counts: at('snippet'),
      dest: 'ingest · beaconing · bot-flood → telemetry view',
      to: '/admin/snippet-telemetry',
    },
    {
      // ADR-074 §3.6 — Email is the 4th producer. A hard bounce, complaint,
      // rejection or in-limbo-past-threshold raises a row here; a MISSING read
      // receipt never does (§3.3). View → deep-links to the Email Validation tab.
      name: 'Email',
      counts: at('email'),
      dest: 'bounce · complaint · in limbo → Email Validation',
      to: '/admin/diags?tab=email',
    },
    {
      // Punchlist #6 — service liveness. This row is the reason the whole
      // feature exists: a service going dark used to be invisible until someone
      // noticed by accident weeks later. It gets its own row (rather than
      // folding into Nexa Platform) because "a service has STOPPED" is a
      // different question from "a service is erroring", and answering it at a
      // glance is the point.
      name: 'Service liveness',
      counts: at('liveness'),
      dest: 'heartbeat missing — a service has stopped → /admin/alerts',
      to: '/admin/alerts',
    },
  ];

  return (
    <section aria-label="Operational alerts" className="alert-widget">
      <div className="aw-top">
        <span className="aw-title">Operational alerts</span>
        {allClear ? (
          <span className="aw-allclear">✓ All clear across every source</span>
        ) : (
          <div className="aw-headline">
            <span className="aw-seg">
              <span className="dot dot-crit" />
              <span className="num num-crit">{total.critical}</span> critical
            </span>
            <span className="aw-seg">
              <span className="dot dot-warn" />
              <span className="num num-warn">{total.warning}</span> warning
            </span>
            <span className="aw-seg">
              <span className="dot dot-info" />
              <span className="num num-info">{total.info}</span> info
            </span>
          </div>
        )}
        <Link className="aw-link" to="/admin/alerts">
          View all alerts →
        </Link>
      </div>

      {partial && (
        <div className="aw-partial" role="status" data-testid="alerts-widget-partial">
          Partial — {failedZones.join(' and ')} did not answer, so {failedZones.length === 1 ? 'its' : 'their'} alerts are
          not in these counts.
        </div>
      )}

      <div className="aw-rows">
        {rows.map((r) => (
          <Link className="aw-row" to={r.to} key={r.name}>
            <div className="aw-row-top">
              <span className="aw-src">{r.name}</span>
              <span className="aw-go">View →</span>
            </div>
            <Counts counts={r.counts} />
            <div className="aw-dest">{r.dest}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
